import {
  FromToDateToolbar,
  PAGINATION_PAGE_SIZES,
  ResizeableContent,
  RouteBreadcrumbs,
  ThemedPage,
  TicketDataTable,
  useApp,
  useDownloadTicketsReport
} from "@ots-frontend/ots-ui";
import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {adjustToTimeZone, ttasSelector, useSearchTtasQuery} from "@ots-frontend/ots-data";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {getTicketsPageFilter} from "../../store/tickets-page/selectors";
import {setTicketsPageFilter} from "../../store/tickets-page/actions";

export type TicketsPageProps = {}

export const TicketsPage = (props: TicketsPageProps) => {

  const dispatch = useDispatch();
  const {regionId, appCategory, baseQueryApi} = useApp();
  const {t} = useTranslation(["translation", "common"]);

  const filter = useAppSelector(getTicketsPageFilter)

  const selectTickets = useMemo(() => {
    return ttasSelector();
  }, []);

  const searchArg = useMemo(() => {
    return {
      fromDttm: adjustToTimeZone(filter.fromDttm).getTime(),
      toDttm: filter.toDttm ? adjustToTimeZone(filter.toDttm).getTime() : undefined,
      regionId: regionId,
      transportCategories: appCategory ? [appCategory.code] : undefined,
    }
  }, [filter, regionId, appCategory])

  const {tickets, isFetching, refetch} = useSearchTtasQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      tickets: selectTickets(result, {}),
      isFetching: result.isFetching,
    })
  });

  const onFilterChange = useCallback((fromDate: Date, toDate: Date | undefined) => {
    dispatch(setTicketsPageFilter({
      fromDttm: fromDate.getTime(),
      toDttm: toDate?.getTime()
    }));
  }, [dispatch]);

  const {
    downloadTicketsReport,
  } = useDownloadTicketsReport({baseQueryApi})

  const onExportClick = useCallback(() => {
    downloadTicketsReport(tickets)
  }, [downloadTicketsReport, tickets])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.tickets.title", "Tickets", {ns: "translation"})}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t('common.action.plural', "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <Menu.Item onClick={() => onExportClick()}>
                          {t("actions.export", "Export", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <FromToDateToolbar
                    defaultValues={{
                      fromDate: new Date(filter.fromDttm),
                      toDate: filter.toDttm ? new Date(filter.toDttm) : undefined,
                    }}
                    loading={isFetching}
                    onSubmit={onFilterChange}/>
                </Card>
                <TicketDataTable
                  withBorder
                  records={tickets}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isFetching}
                  ticketUrlPrefix={"/trucking"}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
